import { initials } from '@lib/initials';
import { absoluteTime, relativeTime } from '@lib/time';

import { describeAuditEntry } from './flag-history-text';

import type { FlagAuditEntry } from '../schemas/flag';

/**
 * The audit trail for one flag.
 *
 * The mock stored pre-written sentences ("Enabled for The Broad"). The API sends
 * structured before/after snapshots instead, which is what makes the log worth
 * keeping — the prose is derived in `flag-history-text.ts`, which is where the
 * only logic in this file lives and where it is tested.
 */
export function FlagHistory({ history }: { history: readonly FlagAuditEntry[] }) {
    if (history.length === 0) {
        return (
            <p className="px-5 py-6 text-center text-[12.5px] text-gray-500">
                Nothing recorded yet. Entries appear here as soon as anyone changes this flag
                through the Backoffice.
            </p>
        );
    }

    return (
        <div className="px-5 py-2">
            {history.map((entry, index) => (
                // The index is part of the key on purpose. Two entries can share
                // a timestamp, action and tenant — the API adds a content-derived
                // nonce to its sort key for exactly that reason, but does not
                // send it — so a key built from the fields alone can collide and
                // drop a row from the list. The list is read-only, append-only
                // and never reordered, so the index is stable here.
                <div
                    key={`${entry.recordedAt}-${entry.action}-${entry.tenantId ?? ''}-${String(index)}`}
                    className="flex gap-3 border-b border-gray-100 py-2.5 text-[12.5px] last:border-0"
                >
                    <span
                        title={entry.actor.email}
                        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px] font-semibold text-gray-600"
                    >
                        {initials(entry.actor.name)}
                    </span>
                    <div className="min-w-0">
                        <div className="text-gray-700">{describeAuditEntry(entry)}</div>
                        {entry.reason !== undefined && (
                            <div className="mt-0.5 italic text-gray-500">“{entry.reason}”</div>
                        )}
                        <div
                            className="mt-0.5 text-gray-400"
                            title={absoluteTime(entry.recordedAt)}
                        >
                            {entry.actor.name} · {relativeTime(entry.recordedAt)}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
